import React, { useContext, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Navbar, Nav } from "react-bootstrap";
import { FaArrowLeft, FaCreditCard } from "react-icons/fa";
import axios from "axios";
import { AuthContext } from "../AuthContext";
import { parseStoredAccessToken } from "../parseStoredAccessToken";
import { site_url } from "../config";

const rupee = (value) =>
  `Rs.${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const last4Digits = (value) => String(value || "").replace(/\D/g, "").slice(0, 4);

const billDateLabel = (value) => {
  if (!value) return "";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const mobileDigits = (value) => {
  let digits = String(value || "").replace(/\D/g, "");
  if (digits.length > 10) digits = digits.slice(-10);
  return digits;
};

export default function CreditCard() {
  const { user, loading } = useContext(AuthContext);
  const [operators, setOperators] = useState([]);
  const [oprCode, setOprCode] = useState("");
  const [last4, setLast4] = useState("");
  const [mobile, setMobile] = useState("");
  const [amount, setAmount] = useState("");
  const [bill, setBill] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [rules, setRules] = useState({
    enabled: true,
    min_wallet_amount: 0,
    max_payment_amount: 49999,
    pay_min: 500,
    pay_max: 49999,
    wallet_balance: null,
    paid_this_month: false,
    this_month: null,
    instant_bonus_percent: 0,
    history: [],
  });

  const authHeaders = () => {
    const token = parseStoredAccessToken(user);
    return {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    };
  };

  const loadRules = async () => {
    const token = parseStoredAccessToken(user);
    if (!token) return;
    try {
      const res = await axios.get(`${site_url}/credit-card/limits`, { headers: authHeaders() });
      const settings = res.data?.settings || {};
      const payMax = Number(res.data?.pay_max ?? settings.max_payment_amount) || 0;
      setRules({
        enabled: settings.enabled !== false,
        min_wallet_amount: Number(settings.min_wallet_amount) || 0,
        max_payment_amount: payMax,
        pay_min: Number(res.data?.pay_min) || 500,
        pay_max: payMax,
        wallet_balance: res.data?.wallet_balance ?? null,
        paid_this_month: !!res.data?.paid_this_month,
        this_month: res.data?.this_month || null,
        instant_bonus_percent: Number(res.data?.instant_bonus_percent) || 0,
        history: res.data?.history || [],
      });
    } catch (err) {
      setError(err.response?.data?.message || "Credit card payment details could not be loaded.");
    }
  };

  const loadOperators = async () => {
    const token = parseStoredAccessToken(user);
    if (!token) return;
    try {
      const res = await axios.get(`${site_url}/credit-card/operators`, { headers: authHeaders() });
      setOperators(res.data?.operators || []);
    } catch (err) {
      setOperators([]);
      setError(err.response?.data?.message || "Credit card banks could not be loaded.");
    }
  };

  useEffect(() => {
    const phone = mobileDigits(user?.phone || user?.mobile || "");
    if (phone) setMobile(phone);
    loadRules();
    loadOperators();
  }, [user]);

  if (loading) {
    return <div style={{ minHeight: "100vh", background: "#f4f7fb" }} />;
  }
  if (!user) {
    return <Navigate to="/signin" replace />;
  }

  const payAmount = Number(amount) || 0;
  const wallet = rules.wallet_balance == null ? null : Number(rules.wallet_balance);
  const maxAmount = Number(rules.pay_max) || 0;
  const minAmount = Math.max(Number(rules.pay_min) || 0, 500);
  const minWallet = Number(rules.min_wallet_amount) || 0;
  const overMax = payAmount > maxAmount && maxAmount > 0;
  const belowMin = payAmount > 0 && payAmount < minAmount;
  const belowMinWallet = wallet != null && wallet < minWallet;
  const shortWallet = wallet != null && payAmount > 0 && wallet < payAmount;
  const selected = operators.find((row) => row.code === oprCode);
  const cardTail = last4Digits(last4).padEnd(4, "•");
  const bonus = payAmount > 0 && rules.instant_bonus_percent > 0
    ? Math.round(((payAmount * rules.instant_bonus_percent) / 100) * 100) / 100
    : 0;
  const canFetch = rules.enabled
    && !rules.paid_this_month
    && !!oprCode
    && last4Digits(last4).length === 4
    && /^[6-9]\d{9}$/.test(mobileDigits(mobile))
    && !fetching
    && !submitting;
  const canPay = canFetch
    && bill
    && payAmount >= minAmount
    && !overMax
    && !belowMinWallet
    && !shortWallet;

  const clearBill = () => setBill(null);

  const place = () => new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve({});
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
      }),
      () => resolve({}),
      { enableHighAccuracy: false, timeout: 4000, maximumAge: 60000 }
    );
  });

  const payload = async () => ({
    opr_code: oprCode,
    operator_name: selected?.name || "",
    cc_last4: last4Digits(last4),
    reg_mobile: mobileDigits(mobile),
    ...(await place()),
  });

  const fetchBill = async () => {
    if (!canFetch) return;
    setError("");
    setResult(null);
    setBill(null);
    setFetching(true);
    try {
      const res = await axios.post(`${site_url}/credit-card/bill`, await payload(), { headers: authHeaders() });
      setBill(res.data);
      if (res.data?.bill_amount && !amount) {
        // setAmount(String(res.data.bill_amount));
      }
    } catch (err) {
      setError(err.response?.data?.message || "Credit card bill could not be fetched.");
    } finally {
      setFetching(false);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!canPay) return;
    setError("");
    setResult(null);
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${site_url}/credit-card/pay`,
        { ...(await payload()), amount: payAmount },
        { headers: authHeaders() }
      );
      setResult(res.data);
      setBill(null);
      setLast4("");
      setAmount("");
      loadRules();
    } catch (err) {
      setError(err.response?.data?.message || "Credit card payment could not be sent.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f4f7fb", paddingBottom: 40 }}>
      <Navbar expand="lg" className="px-3" style={navStyle}>
        <Navbar.Brand className="fw-bold text-white">
          <FaArrowLeft
            onClick={() => window.history.back()}
            className="fs-5 text-white"
            style={{ cursor: "pointer", marginRight: 8 }}
          />
          Credit Card Payment
        </Navbar.Brand>
        <Nav className="ms-auto" />
      </Navbar>

      <div className="container" style={{ maxWidth: 520, marginTop: 16 }}>
        <div style={walletCard}>
          <div>
            <div style={mutedLabel}>Wallet balance</div>
            <div style={walletAmount}>🪙 {wallet != null ? (wallet) : "—"}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={mutedLabel}>Pay range</div>
           
            <div style={{ ...mutedLabel, marginTop: 4 }}>Min wallet 🪙{(minWallet)}</div>
          </div>
        </div>
        <div className="alert alert-success" role="alert">
    Credit card payments are supported for eligible banks up to ₹49,999.
    Multiple payments are allowed, subject to your bank's restrictions.
</div>
        <div className="card shadow-sm" style={{ borderRadius: 16, border: "none" }}>
          <div className="card-body" style={{ padding: 18 }}>
            {!rules.enabled ? (
              <div className="alert alert-warning mb-0">Credit card payment is turned off.</div>
            ) : (
              <form onSubmit={submit}>
                <div style={cardFace}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 12, letterSpacing: 1.2, opacity: 0.9 }}>CREDIT CARD</span>
                    <FaCreditCard />
                  </div>
                  <div style={cardNumber}>•••• &nbsp; •••• &nbsp; •••• &nbsp; {cardTail}</div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, opacity: 0.92 }}>
                    <span>{selected?.name || "Select a bank"}</span>
                    <span>{rules.paid_this_month ? "Used this month" : "Available this month"}</span>
                  </div>
                </div>

                <div style={chipRow}>
                  <span style={chip}>One payment each month</span>
                  <span style={chip}>Pay bill by wallet coins only.</span>
                  <span style={chip}>No partial payment</span>
                </div>

                {rules.paid_this_month && (
                  <div style={notice("info")}>
                    Credit card payment is allowed once this month
                    {rules.this_month?.orderid ? ` · Bill ${rules.this_month.orderid}` : ""}.
                  </div>
                )}

                <label style={fieldLabel}>Bank</label>
                <select
                  className="form-control"
                  style={field}
                  value={oprCode}
                  onChange={(e) => {
                    setOprCode(e.target.value);
                    clearBill();
                  }}
                  required
                >
                  <option value="">Select bank</option>
                  {operators.map((row) => (
                    <option key={row.code} value={row.code}>{row.name}</option>
                  ))}
                </select>

                <label style={fieldLabel}>Last 4 digits of the card</label>
                <input
                  className="form-control"
                  style={{ ...field, letterSpacing: 4, fontWeight: 700 }}
                  inputMode="numeric"
                  autoComplete="cc-number"
                  value={last4Digits(last4)}
                  onChange={(e) => {
                    setLast4(last4Digits(e.target.value));
                    clearBill();
                  }}
                  placeholder="XXXX"
                  required
                />

                <label style={fieldLabel}>Mobile number linked to the card</label>
                <input
                  className="form-control"
                  style={field}
                  inputMode="numeric"
                  autoComplete="tel"
                  value={mobileDigits(mobile)}
                  onChange={(e) => {
                    setMobile(mobileDigits(e.target.value));
                    clearBill();
                  }}
                  placeholder="10-digit mobile"
                  required
                />

                <button
                  type="button"
                  className="btn w-100"
                  style={outlineBtn}
                  disabled={!canFetch}
                  onClick={fetchBill}
                >
                  {fetching ? "Fetching bill..." : "Get bill"}
                </button>

                {bill && (
                  <div style={billCard}>
                    <div style={mutedLabel}>{bill.message || "Bill fetched"}</div>
                    {bill.customer_name && <div style={{ fontWeight: 700, color: "#1d3b53", marginTop: 4 }}>{bill.customer_name}</div>}
                    {bill.bill_number && (
                      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
                        <span style={mutedLabel}>Bill number</span>
                        <span style={{ fontWeight: 700, color: "#1d3b53" }}>{bill.bill_number}</span>
                      </div>
                    )}
                    {bill.bill_date && (
                      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                        <span style={mutedLabel}>Bill date</span>
                        <span style={{ fontWeight: 700, color: "#1d3b53" }}>{billDateLabel(bill.bill_date)}</span>
                      </div>
                    )}
                    {bill.due_date && (
                      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                        <span style={mutedLabel}>Due date</span>
                        <span style={{ fontWeight: 700, color: "#1d3b53" }}>{billDateLabel(bill.due_date)}</span>
                      </div>
                    )}
                    {bill.bill_amount != null && (
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 8 }}>
                        <span style={mutedLabel}>Bill amount</span>
                        <span style={{ fontSize: 20, fontWeight: 800, color: "#1d3b53" }}>{rupee(bill.bill_amount)}</span>
                      </div>
                    )}
                  </div>
                )}

                <label style={fieldLabel}>Enter payment amount</label>
                <input
                  className="form-control"
                  style={field}
                  type="number"
                  min={minAmount}
                  max={maxAmount || undefined}
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  
                  required
                />
                <div style={{ ...mutedLabel, marginTop: -6, marginBottom: 12 }}>
                * Welcome bonus not eligible for CC Bill payments.<br></br>
                * use Refferal bonus for CC Bill payments.
                </div>

                {belowMin && (
                  <div style={notice("warn")}>
                    Minimum credit card bill payment is {rupee(minAmount)}. Please enter {rupee(minAmount)} or more to continue.
                  </div>
                )}
                {belowMinWallet && (
                  <div style={notice("warn")}>
                    Minimum wallet amount is {rupee(minWallet)}. Your wallet is {rupee(wallet)}.
                  </div>
                )}
                {overMax && (
                  <div style={notice("danger")}>
                    Your max credit card payment amount is {rupee(maxAmount)}. Please enter {rupee(maxAmount)} or less to continue.
                  </div>
                )}
                {shortWallet && !belowMinWallet && (
                  <div style={notice("warn")}>
                    Wallet balance is not enough. Online and partial payment are not available. Your wallet is {rupee(wallet)}.
                  </div>
                )}
                {bonus > 0 && (
                  <div style={notice("success")}>
                    Instant bonus 🪙{(bonus)} will be credited to your wallet.
                  </div>
                )}

                <button
                  type="submit"
                  className="btn w-100 text-white"
                  style={payBtn}
                  disabled={!canPay}
                >
                  {submitting ? "Please wait..." : `Pay ${rupee(payAmount)} from wallet`}
                </button>
              </form>
            )}

            {error && <div style={{ ...notice("danger"), marginTop: 14, marginBottom: 0 }}>{error}</div>}
            {result && (
              <div style={{ ...notice(result.status === "failed" ? "danger" : "success"), marginTop: 14, marginBottom: 0 }}>
                <div style={{ fontWeight: 800, textTransform: "capitalize" }}>{result.status}</div>
                <div style={{ marginTop: 2 }}>{result.message}</div>
                {result.orderid && (
                  <div style={receipt}>
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>Purchase bill</div>
                    <ReceiptRow label="Bill" value={result.orderid} />
                    <ReceiptRow label="Amount" value={rupee(result.amount)} />
                    {result.account_no && <ReceiptRow label="Card" value={result.account_no} />}
                  </div>
                )}
                {Number(result.instant_bonus) > 0 && (
                  <div style={{ marginTop: 6 }}>Instant bonus credited: {rupee(result.instant_bonus)}</div>
                )}
                {result.redemption_balance != null && (
                  <div>Wallet balance: {rupee(result.redemption_balance)}</div>
                )}
              </div>
            )}
          </div>
        </div>

        {rules.history.length > 0 && (
          <div className="card shadow-sm mt-3" style={{ borderRadius: 16, border: "none" }}>
            <div className="card-body" style={{ padding: 18 }}>
              <h6 className="fw-bold mb-3">Recent credit card payments</h6>
              {rules.history.map((row, index) => (
                <div
                  key={row.orderid}
                  style={{
                    padding: "10px 0",
                    borderBottom: index === rules.history.length - 1 ? "none" : "1px solid #e6eef5",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                    <strong style={{ color: "#1d3b53" }}>{row.account_no}</strong>
                    <span style={{ fontWeight: 800, color: "#1d3b53" }}>{rupee(row.amount)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 4, alignItems: "center" }}>
                    <span style={mutedLabel}>
                      {row.orderid}
                      {row.created_at ? ` · ${new Date(row.created_at).toLocaleString("en-IN")}` : ""}
                    </span>
                    <span style={statusPill(row.status)}>{row.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ReceiptRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginTop: 4 }}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

const navStyle = {
  background: "#5ca4d6",
  borderBottomLeftRadius: "16px",
  borderBottomRightRadius: "16px",
  padding: "18px 12px",
};

const walletCard = {
  background: "#fff",
  borderRadius: 16,
  padding: "14px 16px",
  marginBottom: 12,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  boxShadow: "0 1px 4px rgba(29, 59, 83, 0.06)",
};

const walletAmount = {
  fontSize: 22,
  fontWeight: 800,
  color: "#1d3b53",
  lineHeight: 1.2,
};

const mutedLabel = {
  fontSize: 12,
  color: "#6b7c8f",
  fontWeight: 600,
};

const cardFace = {
  background: "linear-gradient(135deg, #2f7eaf 0%, #5ca4d6 58%, #8ec4e6 100%)",
  color: "#fff",
  borderRadius: 16,
  padding: "16px 16px 14px",
  marginBottom: 12,
};

const cardNumber = {
  fontSize: 20,
  fontWeight: 700,
  letterSpacing: 1,
  margin: "18px 0 14px",
};

const chipRow = {
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
  marginBottom: 14,
};

const chip = {
  background: "#eef5fb",
  color: "#2f6284",
  borderRadius: 999,
  padding: "4px 10px",
  fontSize: 12,
  fontWeight: 700,
};

const fieldLabel = {
  display: "block",
  fontSize: 13,
  fontWeight: 700,
  color: "#1d3b53",
  marginBottom: 6,
};

const field = {
  borderRadius: 12,
  border: "1px solid #d7e3ee",
  padding: "12px 14px",
  marginBottom: 14,
  background: "#fff",
};

const outlineBtn = {
  borderRadius: 12,
  fontWeight: 700,
  border: "1px solid #5ca4d6",
  color: "#3d8ec4",
  background: "#fff",
  padding: "12px 14px",
  marginBottom: 14,
};

const payBtn = {
  background: "#5ca4d6",
  borderRadius: 12,
  fontWeight: 800,
  padding: "12px 14px",
  marginTop: 4,
};

const billCard = {
  background: "#f4f9fd",
  border: "1px solid #d7e8f4",
  borderRadius: 14,
  padding: "12px 14px",
  marginBottom: 14,
};

const receipt = {
  marginTop: 10,
  paddingTop: 8,
  borderTop: "1px solid rgba(29, 59, 83, 0.12)",
};

const noticeColors = {
  info: { background: "#e8f4fb", color: "#1d5274" },
  warn: { background: "#fff6e8", color: "#8a5a12" },
  danger: { background: "#fdecea", color: "#9b2c2c" },
  success: { background: "#e8f7ef", color: "#1f7a4d" },
};

const notice = (tone) => ({
  ...noticeColors[tone],
  borderRadius: 12,
  padding: "10px 12px",
  fontSize: 13,
  fontWeight: 600,
  marginBottom: 12,
});

const statusPill = (status) => {
  const value = String(status || "").toLowerCase();
  const failed = value === "failed" || value === "failure";
  const ok = value === "success" || value === "paid" || value === "completed";
  return {
    background: failed ? "#fdecea" : ok ? "#e7f6ee" : "#eef3f8",
    color: failed ? "#9b2c2c" : ok ? "#1f7a4d" : "#3d5166",
    borderRadius: 999,
    padding: "2px 8px",
    fontSize: 11,
    fontWeight: 800,
    textTransform: "capitalize",
    whiteSpace: "nowrap",
  };
};
